import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cargomatic/ui';

import EditButton from 'components/v2/buttons/EditButton';
import CapabilityCircle from '../../user/CapabilityCircle';

// Radix's Select throws if a <SelectItem> is given a null or empty-string value.
// A null-valued item (e.g. a "clear"/"not set" option) is therefore rendered with
// this non-empty sentinel and mapped back to its real value on selection.
const NULL_OPTION_VALUE = '__null__';

const toOptionValue = (value) =>
  value === null || value === undefined || value === ''
    ? NULL_OPTION_VALUE
    : value;

class ShipmentDetailDropdown extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showEdit: false,
      valueObj: this.props.value,
    };
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    if (!this.state.showEdit && newProps.value !== this.state.valueObj) {
      this.setState({ valueObj: newProps.value });
    }
  }

  handleSelect = (valueObj) => {
    // Guard against an unmatched selection (e.g. a value not present in items)
    // so we never overwrite the current value with undefined.
    if (!valueObj) {
      return;
    }
    this.setState({ valueObj });
  };

  handleSave = () => {
    this.setState({ showEdit: false }, this.props.onSave(this.state.valueObj));
  };

  renderInlineEditing() {
    const { t } = this.props;
    if (!this.state.showEdit) {
      return null;
    }

    const selectableItems = this.props.items.filter(
      ({ selectable }) => selectable,
    );

    return (
      <div className="" data-testid="pod-status-update-wrapper">
        <Select
          value={toOptionValue(this.state.valueObj?.value)}
          onValueChange={(value) =>
            this.handleSelect(
              selectableItems.find(
                (item) => toOptionValue(item.value) === value,
              ),
            )
          }
          disabled={this.props.disabled}
        >
          <SelectTrigger
            className="mb-4 sm:col-span-1 w-48"
            data-testid="CargomaticSelect-11d8255a"
          >
            <SelectValue placeholder={this.props.placeholder} />
          </SelectTrigger>
          <SelectContent>
            {selectableItems.map((item) => (
              <SelectItem
                key={toOptionValue(item.value)}
                value={toOptionValue(item.value)}
              >
                {t(item.localizationSlug)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div
          data-testid="div-6aa143f4"
          className="flex items-center mt-2 space-x-2"
        >
          <button
            key={2}
            onClick={this.handleSave}
            className="button button-green carrier-shipmentDetailDateItem-editAppointment"
            data-testid="save-pod-status-update"
          >
            {titleize(t('SAVE'))}
          </button>
          <button
            key={1}
            type="button button-gray"
            onClick={() => {
              this.setState({ showEdit: false, valueObj: this.props.value });
            }}
            data-testid="cancel-pod-status-update"
          >
            {t('CANCEL')}
          </button>
        </div>
      </div>
    );
  }

  renderValue() {
    const { t } = this.props;
    if (this.state.showEdit) {
      return null;
    }

    return (
      <div data-testid="div-efa37378" className="flex items-center space-x-2">
        <span data-testid="span-066285cc" className="mb-0 mr-3 ">
          {this.state.valueObj?.localizationSlug
            ? t(this.state.valueObj.localizationSlug)
            : this.props.placeholder}
        </span>
        {this.props.showIcon && (
          <CapabilityCircle checked={this.props.iconValue} />
        )}
        {this.props.editable && (
          <EditButton
            title={t('EDIT_DATE')}
            disabled={!this.props.editable}
            onClick={() => this.setState({ showEdit: true })}
            dataTestId="edit-pod-status"
          />
        )}
      </div>
    );
  }

  render() {
    return (
      <div data-testid="div-486af12d">
        {this.state.showEdit ? this.renderInlineEditing() : this.renderValue()}
      </div>
    );
  }
}

ShipmentDetailDropdown.propTypes = {
  items: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  editable: PropTypes.bool,
  showIcon: PropTypes.bool,
  iconValue: PropTypes.bool,
  placeholder: PropTypes.string,
  t: PropTypes.func,
};

ShipmentDetailDropdown.defaultProps = {
  disabled: false,
  showIcon: false,
  iconValue: false,
  editable: false,
  placeholder: '',
  // withTranslation injects `t`; fall back to an identity so the component never
  // crashes if it is rendered without the i18n HOC (e.g. in tests).
  t: (key) => key,
};

export default withTranslation()(ShipmentDetailDropdown);
